import { useCallback, useEffect, useState } from 'react';

export type SortState = { property?: string; order?: 'asc' | 'desc' } | Record<string, never>;

export type FetchReportResult = {
  data_set: unknown[];
  rows_count: number;
};

export type FetchReportFn = (
  page: number,
  pageSize: number,
  search: Record<string, unknown>,
  sorting: SortState
) => Promise<{ data?: FetchReportResult }>;

export type UsePaginatedReportOptions = {
  fetchData: FetchReportFn;
  initialSearch?: Record<string, unknown>;
  formatRow?: (raw: unknown) => unknown;
  resetPageOnChange?: boolean;
  initialPageSize?: number;
};

function mapDataSetToRows(data_set: unknown, formatRow: UsePaginatedReportOptions['formatRow']): unknown[] {
  if (!Array.isArray(data_set)) {
    return [];
  }
  if (formatRow) {
    return data_set.map(formatRow);
  }
  return data_set;
}

export type UsePaginatedReportReturn = {
  data: unknown[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  search: Record<string, unknown>;
  sorting: SortState;
  onSearch: (name: string, value: unknown) => void;
  handleSortingChange: (newSorting: SortState & { property?: string; order?: 'asc' | 'desc' }) => void;
  nextPage: (page: number, selectedSize: number) => Promise<void>;
  getExportData: (formatExportData: (dataSet: unknown[]) => unknown[]) => () => Promise<unknown[]>;
  refresh: () => Promise<void>;
};

function usePaginatedReport(options: UsePaginatedReportOptions): UsePaginatedReportReturn {
  const { fetchData, initialSearch = {}, formatRow, resetPageOnChange = true, initialPageSize = 20 } = options;

  const [data, setData] = useState<unknown[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [search, setSearch] = useState<Record<string, unknown>>(initialSearch);
  const [sorting, setSorting] = useState<SortState>({});

  const syncData = useCallback(
    async (page: number, size: number) => {
      setIsLoading(true);
      try {
        const response = await fetchData(page, size, search, sorting);
        const { data_set = [], rows_count = 0 } = response?.data ?? {};
        const rows = mapDataSetToRows(data_set, formatRow);
        setData(rows);
        setTotalCount(rows_count);
        setCurrentPage(page);
        setPageSize(size);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchData, search, sorting, formatRow]
  );

  const refresh = useCallback(() => {
    return syncData(currentPage, pageSize);
  }, [syncData, currentPage, pageSize]);

  useEffect(() => {
    const page = resetPageOnChange ? 0 : currentPage;
    syncData(page, pageSize);
  }, [search, sorting]);

  const nextPage = useCallback(
    async (page: number, selectedSize: number) => {
      setIsLoading(true);
      try {
        const response = await fetchData(page, selectedSize, search, sorting);
        const { data_set = [], rows_count = 0 } = response?.data ?? {};
        const rows = mapDataSetToRows(data_set, formatRow);
        setData(rows);
        setTotalCount(rows_count);
        setCurrentPage(page);
        setPageSize(selectedSize);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchData, search, sorting, formatRow]
  );

  const onSearch = useCallback((name: string, value: unknown) => {
    setSearch((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortState & { property?: string; order?: 'asc' | 'desc' }) => {
      if (!newSorting?.property) {
        setSorting({});
        return;
      }
      setSorting({
        property: newSorting.property,
        order: newSorting.order,
      });
      if (resetPageOnChange) {
        setCurrentPage(0);
      }
    },
    [resetPageOnChange]
  );

  const getExportData = useCallback(
    (formatExportData: (dataSet: unknown[]) => unknown[]) => {
      return async (): Promise<unknown[]> => {
        setIsLoading(true);
        try {
          const response = await fetchData(0, Number(totalCount) || 9999, search, sorting);
          const data_set = response?.data?.data_set ?? [];
          return formatExportData(Array.isArray(data_set) ? data_set : []);
        } finally {
          setIsLoading(false);
        }
      };
    },
    [fetchData, search, sorting, totalCount]
  );

  return {
    data,
    isLoading,
    totalCount,
    currentPage,
    pageSize,
    search,
    sorting,
    onSearch,
    handleSortingChange,
    nextPage,
    getExportData,
    refresh,
  };
}

export { usePaginatedReport };
